export const IMPERSONATION_BLOCKED_EVENT = "productlane:impersonation-blocked"

export type PageEntity = "thread" | "contact" | "company" | "issue" | "project"

export interface PageContext {
	entity: PageEntity
	id: string
}

const ROUTES: Array<{ pattern: RegExp; entity: PageEntity }> = [
	{ pattern: /^\/threads\/([^/?#]+)/, entity: "thread" },
	{ pattern: /^\/inbox\/[^/]+\/([^/?#]+)/, entity: "thread" },
	{ pattern: /^\/feedback\/([^/?#]+)/, entity: "thread" },
	{ pattern: /^\/people\/([^/?#]+)/, entity: "contact" },
	{ pattern: /^\/customers\/([^/?#]+)/, entity: "company" },
	{ pattern: /^\/companies\/([^/?#]+)/, entity: "company" },
	{ pattern: /^\/issues\/([^/?#]+)/, entity: "issue" },
	{ pattern: /^\/projects\/([^/?#]+)/, entity: "project" },
]

export function parsePageContext(pathname: string): PageContext | null {
	for (const { pattern, entity } of ROUTES) {
		const id = pattern.exec(pathname)?.[1]
		if (id) return { entity, id: decodeURIComponent(id) }
	}
	return null
}

export function formatElapsed(
	startedAt: string | null | undefined,
	now: number,
) {
	if (!startedAt) return null
	const start = Date.parse(startedAt)
	if (Number.isNaN(start)) return null
	const minutes = Math.max(0, Math.floor((now - start) / 60_000))
	if (minutes < 60) return `${minutes} min`
	const hours = Math.floor(minutes / 60)
	const rest = minutes % 60
	return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

export function buildDebugMarkdown(input: {
	workspaceName: string
	workspaceId: string
	plan?: string | null
	userName: string
	email: string
	role: string
	userId: string
	pathname: string
	stripeCustomerId?: string | null
}) {
	const lines = [
		`**${input.workspaceName}**${input.plan ? `, ${input.plan}` : ""}`,
		`User: ${input.userName || input.email}, ${input.email}, ${input.role}`,
		`userId: ${input.userId}`,
		`workspaceId: ${input.workspaceId}`,
		`page: ${input.pathname}`,
	]
	if (input.stripeCustomerId) lines.push(`stripe: ${input.stripeCustomerId}`)
	return lines.join("\n")
}

export function copyThreadCommand(threadId: string) {
	return `pnpm cli copy-thread --thread ${threadId}`
}

export const ROLE_LABELS: Record<string, string> = {
	ADMIN: "Admin",
	USER: "Member",
	VIEWER: "Viewer",
}

export const roleLabel = (role: string | null | undefined) =>
	role ? (ROLE_LABELS[role] ?? role) : ""

export function stripeDashboardUrl(
	subscriptionId: string | null | undefined,
	customerId: string | null | undefined,
) {
	if (subscriptionId) {
		return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`
	}
	if (customerId) return `https://dashboard.stripe.com/customers/${customerId}`
	return null
}

export const currentPath = () =>
	`${window.location.pathname}${window.location.search}`
